/**
 * ResetPasswordPage
 * Feature: 076-unified-login-tokens
 *
 * Landing page for password reset links. User receives a link with a nonce
 * and a PIN in the email. They must enter the PIN to verify ownership,
 * then can set a new password.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Container, Stack } from '@/components/layout';
import { Heading, Text } from '@/components/atoms';
import { useResetPasswordWithPinGraphQL } from '@/hooks/graphql/useLoginTokensGraphQL';
import logger from '@/utils/logger';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [nonce, setNonce] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [step, setStep] = useState<'pin' | 'password'>('pin');

  const { resetPassword, loading, error } = useResetPasswordWithPinGraphQL();

  // Extract and validate nonce from URL
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam || !/^[0-9a-f]{64}$/i.test(tokenParam)) {
      setErrorMessage('Invalid or missing token. Please check your link.');
      logger.warn('Auth', 'Invalid reset token format', {
        hasToken: !!tokenParam,
        length: tokenParam?.length,
      });
    } else {
      setNonce(tokenParam);
    }
  }, [searchParams]);

  // Handle PIN input (only allow digits, max 6)
  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
  };

  // Validate PIN and move to password step
  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!/^[0-9]{6}$/.test(pin)) {
      setErrorMessage('PIN must be 6 digits');
      return;
    }

    setStep('password');
  };

  // Handle password reset
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!nonce) {
      setErrorMessage('Invalid token');
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters');
      return;
    }

    if (newPassword.length > 128) {
      setErrorMessage('Password must be less than 128 characters');
      return;
    }

    try {
      const result = await resetPassword(nonce, pin, newPassword);

      if (result?.success) {
        logger.info('Auth', 'Password reset successful, redirecting to login');
        navigate('/login?resetSuccess=true');
      }
    } catch (err: unknown) {
      logger.warn('Auth', 'Password reset failed', { error: err });
      // Error is handled by the hook
    }
  };

  // Show error state if token is invalid
  if (!nonce && errorMessage) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Invalid Link</Heading>
            <Text className="mt-4 text-destructive">{errorMessage}</Text>
            <Text className="mt-2 text-content-secondary">
              Please request a new password reset link.
            </Text>
            <Link
              to="/login"
              className="mt-4 inline-block text-primary hover:underline"
            >
              Back to login
            </Link>
          </div>
        </Stack>
      </Container>
    );
  }

  // Loading state while validating
  if (!nonce) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Text>Validating link...</Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Get error message from GraphQL error
  const graphQLError = error?.graphQLErrors?.[0]?.message || error?.message;

  // Step 1: PIN entry
  if (step === 'pin') {
    return (
      <Container maxWidth="sm">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Reset Your Password</Heading>
            <Text className="mt-2 text-content-secondary">
              Enter the 6-digit PIN from your email to continue.
            </Text>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-6">
            {/* PIN Input */}
            <div className="space-y-2">
              <label
                htmlFor="pin"
                className="block text-sm font-medium text-foreground"
              >
                PIN
              </label>
              <input
                id="pin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                value={pin}
                onChange={handlePinChange}
                placeholder="000000"
                className="w-full px-4 py-3 text-2xl font-mono tracking-widest text-center border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={6}
                autoFocus
              />
            </div>

            {/* Error message */}
            {errorMessage && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
                {errorMessage}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={pin.length !== 6}
              className={`w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors ${
                pin.length !== 6
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              Continue
            </button>
          </form>

          {/* Help text */}
          <div className="text-center text-sm text-muted-foreground">
            <p>The PIN was sent to your email along with this link.</p>
            <Link
              to="/login"
              className="mt-2 block text-primary hover:underline"
            >
              Didn&apos;t receive the email? Back to login
            </Link>
          </div>

          <Link
            to="/login"
            className="text-center text-primary hover:underline text-sm"
          >
            &larr; Back to login
          </Link>
        </Stack>
      </Container>
    );
  }

  // Step 2: Password entry
  return (
    <Container maxWidth="sm">
      <Stack direction="vertical" spacing={6} className="py-12">
        <div className="text-center">
          <Heading level="h1">Set New Password</Heading>
          <Text className="mt-2 text-content-secondary">
            Enter your new password below.
          </Text>
        </div>

        <form onSubmit={handlePasswordSubmit} className="space-y-6">
          {/* New Password */}
          <div className="space-y-2">
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-foreground"
            >
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              minLength={8}
              maxLength={128}
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-foreground"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              minLength={8}
              maxLength={128}
              disabled={loading}
            />
          </div>

          {/* Error message */}
          {(errorMessage || graphQLError) && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
              {errorMessage || graphQLError}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || !newPassword || !confirmPassword}
            className={`w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors ${
              loading || !newPassword || !confirmPassword
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        {/* Back button */}
        <button
          onClick={() => setStep('pin')}
          className="text-center text-primary hover:underline text-sm"
        >
          &larr; Back to PIN entry
        </button>
      </Stack>
    </Container>
  );
}
